/**
 * v2 Hook Handlers — Turn Boundaries, Sub-agents, Compaction, Tool Failures
 *
 * Handles the 7 new Claude Code events (taxonomy v2):
 *   UserPromptSubmit    → emit turn span (kind: chain); push onto parentStack
 *   Stop                → pop chain span (NOT root); validates kind before pop
 *   SubagentStart       → emit agent span (kind: agent); push onto parentStack
 *   SubagentStop        → pop agent span; validates kind before pop
 *   PreCompact          → open custom span (kind: custom); push onto parentStack
 *   PostCompact         → pop + close the custom compaction span with summary in output
 *   PostToolUseFailure  → close matching open tool span with status: error
 *
 * Kind mapping to BE SpanKindSchema (strict 15-value enum):
 *   turn spans    → 'chain'  (orchestration container)
 *   compaction    → 'custom' (non-standard observability event)
 *   session root  → 'agent'
 *   subagents     → 'agent'
 *   tool calls    → 'tool'
 */

import { randomUUID } from 'crypto';
import {
  readState,
  writeState,
  pushParent,
  popParent,
  peekParent,
  peekParentKind,
} from './state.js';
import { sendBatchTrace } from './api.js';
import { readStdin, getString, serializePayload } from './handlers-core.js';

// ============================================================================
// UserPromptSubmit — emit turn span; push onto parentStack
// ============================================================================

export async function handleUserPromptSubmit(): Promise<void> {
  const input = await readStdin();
  const sessionId = getString(input, 'session_id', 'sessionId');
  if (!sessionId) throw new Error('Missing session_id in stdin');

  const now = new Date().toISOString();
  const state = readState(sessionId) ?? {
    traceId: `cc-${sessionId}`,
    sessionId,
    startTime: now,
    openSpans: {},
    parentStack: [],
    parentStackKinds: [],
  };

  const parentSpanId = peekParent(state);
  const turnSpanId = randomUUID();

  const prompt = input.prompt ?? input.message ?? input.text;
  const inputPayload = serializePayload(prompt ? { prompt } : undefined);

  pushParent(state, turnSpanId, 'chain');
  writeState(sessionId, state);

  await sendBatchTrace([
    {
      traceId: state.traceId,
      sessionId,
      name: 'Claude Code Session',
      source: 'sdk',
      startTime: state.startTime,
      status: 'running',
      spans: [
        {
          spanId: turnSpanId,
          name: 'User turn',
          kind: 'chain',
          startTime: now,
          status: 'running',
          ...(inputPayload !== undefined ? { input: inputPayload } : {}),
          ...(parentSpanId !== null ? { parentSpanId } : {}),
        },
      ],
    },
  ]);
}

// ============================================================================
// Stop — pop + close turn span (NOT root)
// ============================================================================

export async function handleStop(): Promise<void> {
  const input = await readStdin();
  const sessionId = getString(input, 'session_id', 'sessionId');
  if (!sessionId) throw new Error('Missing session_id in stdin');

  const now = new Date().toISOString();
  const state = readState(sessionId);
  if (!state) return;

  // Don't pop the root span (depth <= 1).
  if (state.parentStack.length <= 1) return;

  // Safety: only pop if the top is a chain (turn) span.
  // If top is 'agent' (subagent that didn't close), log a warning and skip.
  const topKind = peekParentKind(state);
  if (topKind !== 'chain') {
    process.stderr.write(
      `[mutagent hooks] Warning: Stop fired but stack top is kind="${topKind ?? 'unknown'}" (expected "chain"). Skipping pop.\n`
    );
    return;
  }

  const turnSpanId = popParent(state);
  writeState(sessionId, state);

  if (!turnSpanId) return;

  // Attach whatever the Stop payload can tell us: stop_reason, assistant response text.
  const stopReason =
    getString(input, 'stop_reason', 'stopReason') ||
    (input.stop_reason != null ? String(input.stop_reason) : undefined);
  const assistantText =
    input.message ?? input.assistant_message ?? input.response ?? input.text;
  const outputValue = assistantText ?? (stopReason ? { stop_reason: stopReason } : undefined);
  const turnOutput = serializePayload(outputValue);

  await sendBatchTrace([
    {
      traceId: state.traceId,
      sessionId,
      name: 'Claude Code Session',
      source: 'sdk',
      startTime: state.startTime,
      status: 'running',
      spans: [
        {
          spanId: turnSpanId,
          name: 'User turn',
          kind: 'chain',
          startTime: now,
          endTime: now,
          status: 'completed',
          parentSpanId: peekParent(state), // parent (root or outer agent) now at top
          ...(turnOutput !== undefined ? { output: turnOutput } : {}),
        },
      ],
    },
  ]);
}

// ============================================================================
// SubagentStart — emit agent span; push onto parentStack
// ============================================================================

export async function handleSubagentStart(): Promise<void> {
  const input = await readStdin();
  const sessionId = getString(input, 'session_id', 'sessionId');
  if (!sessionId) throw new Error('Missing session_id in stdin');

  const now = new Date().toISOString();
  const state = readState(sessionId) ?? {
    traceId: `cc-${sessionId}`,
    sessionId,
    startTime: now,
    openSpans: {},
    parentStack: [],
    parentStackKinds: [],
  };

  const parentSpanId = peekParent(state);
  const agentSpanId = randomUUID();

  const agentType = getString(input, 'agent_type', 'agentType') || 'subagent';
  const agentId = getString(input, 'agent_id', 'agentId');
  const agentName = agentId ? `${agentType}:${agentId}` : agentType;

  // Attach the subagent's initial prompt/task as span input when available.
  const promptValue =
    input.prompt ??
    input.message ??
    input.system_prompt ??
    input.task ??
    input.tool_response ??
    input.toolResponse;
  const agentInput = serializePayload(promptValue);

  pushParent(state, agentSpanId, 'agent');
  writeState(sessionId, state);

  await sendBatchTrace([
    {
      traceId: state.traceId,
      sessionId,
      name: 'Claude Code Session',
      source: 'sdk',
      startTime: state.startTime,
      status: 'running',
      spans: [
        {
          spanId: agentSpanId,
          name: agentName,
          kind: 'agent',
          startTime: now,
          status: 'running',
          ...(parentSpanId !== null ? { parentSpanId } : {}),
          ...(agentInput !== undefined ? { input: agentInput } : {}),
        },
      ],
    },
  ]);
}

// ============================================================================
// SubagentStop — pop + close subagent span
// ============================================================================

export async function handleSubagentStop(): Promise<void> {
  const input = await readStdin();
  const sessionId = getString(input, 'session_id', 'sessionId');
  if (!sessionId) throw new Error('Missing session_id in stdin');

  const now = new Date().toISOString();
  const state = readState(sessionId);
  if (!state) {
    process.stderr.write(
      `[mutagent hooks] Warning: SubagentStop received but no state found for session ${sessionId}\n`
    );
    return;
  }

  if (state.parentStack.length <= 1) {
    process.stderr.write(
      `[mutagent hooks] Warning: SubagentStop with no matching SubagentStart in session ${sessionId}\n`
    );
    return;
  }

  // Safety: only pop if the top is an agent span.
  const topKind = peekParentKind(state);
  if (topKind !== 'agent') {
    process.stderr.write(
      `[mutagent hooks] Warning: SubagentStop but stack top is kind="${topKind ?? 'unknown'}" (expected "agent"). Skipping pop.\n`
    );
    return;
  }

  const agentSpanId = popParent(state);
  const newParent = peekParent(state);
  writeState(sessionId, state);

  if (!agentSpanId) return;

  // Attach the subagent's final message/summary as span output when available.
  // Use the same tool_response / stop_reason field-sniff pattern as handleStop.
  const finalMessage =
    input.message ??
    input.assistant_message ??
    input.response ??
    input.tool_response ??
    input.toolResponse;
  const stopReason = getString(input, 'stop_reason', 'stopReason');
  const outputValue = finalMessage ?? (stopReason ? { stop_reason: stopReason } : undefined);
  const agentOutput = serializePayload(outputValue);

  await sendBatchTrace([
    {
      traceId: state.traceId,
      sessionId,
      name: 'Claude Code Session',
      source: 'sdk',
      startTime: state.startTime,
      status: 'running',
      spans: [
        {
          spanId: agentSpanId,
          name: 'subagent',
          kind: 'agent',
          startTime: now,
          endTime: now,
          status: 'completed',
          ...(newParent !== null ? { parentSpanId: newParent } : {}),
          ...(agentOutput !== undefined ? { output: agentOutput } : {}),
        },
      ],
    },
  ]);
}

// ============================================================================
// PreCompact — open compaction span (kind: custom); push onto parentStack
// ============================================================================

export async function handlePreCompact(): Promise<void> {
  const input = await readStdin();
  const sessionId = getString(input, 'session_id', 'sessionId');
  if (!sessionId) throw new Error('Missing session_id in stdin');

  const now = new Date().toISOString();
  const state = readState(sessionId) ?? {
    traceId: `cc-${sessionId}`,
    sessionId,
    startTime: now,
    openSpans: {},
    parentStack: [],
    parentStackKinds: [],
  };

  const parentSpanId = peekParent(state);
  const compactSpanId = randomUUID();

  pushParent(state, compactSpanId, 'custom');
  writeState(sessionId, state);

  await sendBatchTrace([
    {
      traceId: state.traceId,
      sessionId,
      name: 'Claude Code Session',
      source: 'sdk',
      startTime: state.startTime,
      status: 'running',
      spans: [
        {
          spanId: compactSpanId,
          name: 'Compaction',
          kind: 'custom',
          startTime: now,
          status: 'running',
          ...(parentSpanId !== null ? { parentSpanId } : {}),
        },
      ],
    },
  ]);
}

// ============================================================================
// PostCompact — pop + close compaction span (kind: custom) with summary in output
// ============================================================================

export async function handlePostCompact(): Promise<void> {
  const input = await readStdin();
  const sessionId = getString(input, 'session_id', 'sessionId');
  if (!sessionId) throw new Error('Missing session_id in stdin');

  const now = new Date().toISOString();
  const state = readState(sessionId);
  if (!state) {
    process.stderr.write(
      `[mutagent hooks] Warning: PostCompact received but no state found for session ${sessionId}\n`
    );
    return;
  }

  if (state.parentStack.length <= 1) {
    process.stderr.write(
      `[mutagent hooks] Warning: PostCompact with no matching PreCompact in session ${sessionId}\n`
    );
    return;
  }

  const compactSpanId = popParent(state);
  const newParent = peekParent(state);
  writeState(sessionId, state);

  if (!compactSpanId) return;

  const summary = input.summary ?? input.compaction_summary ?? input.compactionSummary;
  const outputPayload = serializePayload(summary ?? 'Context compacted');

  await sendBatchTrace([
    {
      traceId: state.traceId,
      sessionId,
      name: 'Claude Code Session',
      source: 'sdk',
      startTime: state.startTime,
      status: 'running',
      spans: [
        {
          spanId: compactSpanId,
          name: 'Compaction',
          kind: 'custom',
          startTime: now,
          endTime: now,
          status: 'completed',
          ...(outputPayload !== undefined ? { output: outputPayload } : {}),
          ...(newParent !== null ? { parentSpanId: newParent } : {}),
        },
      ],
    },
  ]);
}

// ============================================================================
// PostToolUseFailure — close matching open tool span with status: error
// ============================================================================

export async function handlePostToolUseFailure(): Promise<void> {
  const input = await readStdin();
  const sessionId = getString(input, 'session_id', 'sessionId');
  const toolName = getString(input, 'tool_name', 'toolName') || 'unknown';
  if (!sessionId) throw new Error('Missing session_id in stdin');

  const now = new Date().toISOString();
  const state = readState(sessionId);
  if (!state) {
    process.stderr.write(
      `[mutagent hooks] Warning: PostToolUseFailure received but no state found for session ${sessionId}\n`
    );
    return;
  }

  const traceId = state.traceId;
  const startTime = state.startTime;

  // Find matching open span by toolName (LIFO)
  let matchedSpanId: string | null = null;
  let matchedSpanStart: string = now;
  let matchedKey: string | null = null;

  const entries = Object.entries(state.openSpans);
  for (let i = entries.length - 1; i >= 0; i--) {
    const entry = entries[i];
    if (entry && entry[1].toolName === toolName) {
      matchedSpanId = entry[1].spanId;
      matchedSpanStart = entry[1].startTime;
      matchedKey = entry[0];
      break;
    }
  }

  if (!matchedSpanId) {
    process.stderr.write(
      `[mutagent hooks] Warning: PostToolUseFailure — no matching open span for tool "${toolName}"\n`
    );
    return;
  }

  state.openSpans = Object.fromEntries(
    Object.entries(state.openSpans).filter(([k]) => k !== matchedKey)
  );
  writeState(sessionId, state);

  // Claude Code nests failure details inside tool_response (same envelope as PostToolUse).
  // Extract the most descriptive error text we can find.
  const toolResponse = input.tool_response ?? input.toolResponse;
  let reason = 'Tool use failed';
  if (typeof toolResponse === 'object' && toolResponse !== null) {
    const nested = (toolResponse as Record<string, unknown>).error ?? (toolResponse as Record<string, unknown>).stderr;
    if (nested != null && String(nested).length > 0) {
      reason = String(nested);
    }
  } else {
    const fallback = getString(input, 'error', 'reason', 'message');
    if (fallback.length > 0) reason = fallback;
  }
  const errorPayload = serializePayload(reason);

  await sendBatchTrace([
    {
      traceId,
      sessionId,
      name: 'Claude Code Session',
      source: 'sdk',
      startTime,
      status: 'running',
      spans: [
        {
          spanId: matchedSpanId,
          name: toolName,
          kind: 'tool',
          startTime: matchedSpanStart,
          endTime: now,
          status: 'error',
          ...(errorPayload !== undefined ? { output: errorPayload } : {}),
        },
      ],
    },
  ]);
}
